// src/components/leads/LeadConversation.jsx
// One timeline of everything said to/with a lead: texts (linked by leadId),
// emails sent through TES (matched by the contact's email), and logged
// activities. Read-only; shared by the buyer and listing lead drawers.
import { useEffect, useMemo, useState } from "react";
import { MessageSquare, Mail, StickyNote, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStateValue } from "../../context/StateProvider";
import { subscribeTextThread, canUseTexting } from "../../utils/texting";
import { getEmailsToAddress, getContactsByIds } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import { mergeConversation } from "../../utils/leadConversation";
import { formatDate } from "../../utils/helper";
import MailDetailPopup from "../settings/email-log-detail";

export default function LeadConversation({ lead }) {
  const [{ user }] = useStateValue();
  const navigate = useNavigate();
  const [texts, setTexts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [openEmail, setOpenEmail] = useState(null);

  useEffect(() => {
    if (!lead?.id) return;
    const unsub = subscribeTextThread({ leadId: lead.id }, setTexts);
    return () => unsub && unsub();
  }, [lead?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!lead?.contactId) { setEmails([]); return; }
      try {
        const [raw] = await getContactsByIds([lead.contactId]);
        const c = normalizeContact(raw);
        if (cancelled) return;
        const rows = c?.email ? await getEmailsToAddress(c.email) : [];
        if (!cancelled) setEmails(rows);
      } catch (err) {
        console.error("Conversation email load failed:", err);
        if (!cancelled) setEmails([]);
      }
    })();
    return () => { cancelled = true; };
  }, [lead?.contactId]);

  const timeline = useMemo(
    () => mergeConversation({ texts, emails, activities: lead?.activities || [] }),
    [texts, emails, lead?.activities],
  );

  const when = (at) => (at ? formatDate(at) : "");

  return (
    <div>
      {canUseTexting(user) && texts.length > 0 && (
        <button
          onClick={() => navigate("/dashboard/texting")}
          className="text-xs font-semibold text-slate-500 hover:text-slate-800 mb-2"
        >
          Open in Texting
        </button>
      )}
      {timeline.length === 0 ? (
        <p className="text-xs text-slate-400">
          Nothing yet. Texts, sent emails, and logged activity will show up here.
        </p>
      ) : (
        <ul className="space-y-2">
          {timeline.map((item) => (
            <li key={item.id} className="text-sm border-l-2 border-slate-200 pl-3">
              {item.kind === "text" && (
                <>
                  <div className="flex justify-between">
                    <span className="font-medium text-slate-700 inline-flex items-center gap-1.5">
                      <MessageSquare size={13} className="text-sky-600" />
                      {item.direction === "inbound" ? (
                        <>Text received <ArrowDownLeft size={12} className="text-emerald-600" /></>
                      ) : (
                        <>Text sent <ArrowUpRight size={12} className="text-slate-400" /></>
                      )}
                    </span>
                    <span className="text-[11px] text-slate-400">{when(item.at)}</span>
                  </div>
                  <div className="text-slate-600 whitespace-pre-wrap">{item.body}</div>
                </>
              )}
              {item.kind === "email" && (
                <button
                  onClick={() => setOpenEmail(item.email)}
                  className="w-full text-left"
                >
                  <div className="flex justify-between">
                    <span className="font-medium text-slate-700 inline-flex items-center gap-1.5">
                      <Mail size={13} className="text-linkColor" /> Email sent
                    </span>
                    <span className="text-[11px] text-slate-400">{when(item.at)}</span>
                  </div>
                  <div className="text-slate-600 truncate underline decoration-slate-300">
                    {item.subject}
                  </div>
                </button>
              )}
              {item.kind === "activity" && (
                <>
                  <div className="flex justify-between">
                    <span className="font-medium text-slate-700 inline-flex items-center gap-1.5">
                      <StickyNote size={13} className="text-amber-600" /> {item.type}
                    </span>
                    <span className="text-[11px] text-slate-400">{when(item.at)}</span>
                  </div>
                  {item.note && <div className="text-slate-600">{item.note}</div>}
                  {item.byName && (
                    <div className="text-[11px] text-slate-400">by {item.byName}</div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {openEmail && (
        <MailDetailPopup mailData={openEmail} onClose={() => setOpenEmail(null)} />
      )}
    </div>
  );
}
